import { Component, OnInit } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { SharedModule } from "@shared/shared.module";
import { CommonApiService } from "src/app/services/common-api.service";
import { APIURL, LOCAL_STORAGE_KEYS } from "src/app/global/constant";
import { finalize } from "rxjs";

@Component({
  selector: "app-help-center",
  templateUrl: "./help-center.component.html",
  styleUrls: ["./help-center.component.scss"],
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule, FormsModule],
})
export class HelpCenterComponent implements OnInit {
  expandedSections: Set<string> = new Set(["getting-started"]);
  expandedStages: Set<string> = new Set();

  // Contact form
  contactForm = {
    name: "",
    email: "",
    subject: "",
    message: "",
  };
  attachments: File[] = [];
  isSubmitting = false;
  submitSuccess = false;
  submitError = "";

  constructor(
    private commonApiService: CommonApiService,
    private location: Location,
  ) {}

  goBack(): void {
    this.location.back();
  }

  ngOnInit(): void {
    const hash = window.location.hash.substring(1);
    if (hash) {
      this.expandedSections.add(hash);
    }
    // Pre-fill from logged-in user
    const loginUser = localStorage.getItem(LOCAL_STORAGE_KEYS.loginUserDetails);
    if (loginUser) {
      try {
        const parsed = JSON.parse(loginUser);
        const u = parsed?.companyUser || parsed;
        this.contactForm.name = `${u?.firstname || ""} ${u?.lastname || ""}`.trim();
        this.contactForm.email = u?.email || "";
      } catch {}
    }
  }

  toggleSection(sectionId: string): void {
    if (this.expandedSections.has(sectionId)) {
      this.expandedSections.delete(sectionId);
    } else {
      this.expandedSections.add(sectionId);
    }
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections.has(sectionId);
  }

  toggleStage(stageId: string, event: Event): void {
    event.stopPropagation();
    if (this.expandedStages.has(stageId)) {
      this.expandedStages.delete(stageId);
    } else {
      this.expandedStages.add(stageId);
    }
  }

  isStageExpanded(stageId: string): boolean {
    return this.expandedStages.has(stageId);
  }

  navigateToSection(sectionId: string): void {
    this.expandedSections.add(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files);
      this.attachments = [...this.attachments, ...newFiles];
      input.value = "";
    }
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  isFormValid(): boolean {
    return !!(
      this.contactForm.name.trim() &&
      this.contactForm.email.trim() &&
      this.contactForm.subject.trim() &&
      this.contactForm.message.trim()
    );
  }

  submitContactForm(): void {
    if (!this.isFormValid() || this.isSubmitting) return;

    const formData = new FormData();
    formData.append("name", this.contactForm.name.trim());
    formData.append("email", this.contactForm.email.trim());
    formData.append("subject", this.contactForm.subject.trim());
    formData.append("message", this.contactForm.message.trim());
    this.attachments.forEach((file) => formData.append("attachments", file));

    this.isSubmitting = true;
    this.submitError = "";
    this.commonApiService
      .post(APIURL.contactSupport, formData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.submitSuccess = true;
          this.contactForm = { name: "", email: "", subject: "", message: "" };
          this.attachments = [];
        },
        error: () => {
          this.submitError =
            "Something went wrong. Please try again or email us directly at info@dairector.studio";
        },
      });
  }

  resetForm(): void {
    this.submitSuccess = false;
    this.submitError = "";
    this.contactForm = { name: "", email: "", subject: "", message: "" };
    this.attachments = [];
  }
}
